import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { startOfWeek, endOfWeek, format } from "date-fns";

export interface UserJourney {
  id: string;
  week_start: string;
  week_end: string;
  mood_average: number;
  mood_improvement: number;
  achievements: string[];
  reflections: string;
}

export const useUserJourney = () => {
  const [journeyData, setJourneyData] = useState<UserJourney | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const loadJourneyData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setIsLoading(false);
        return;
      }

      const weekStart = startOfWeek(new Date());
      const weekEnd = endOfWeek(new Date());

      // First, get the mood averages for the week
      const { data: moodData, error: moodError } = await supabase
        .from('daily_moods')
        .select('mood_score')
        .gte('date', format(weekStart, 'yyyy-MM-dd'))
        .lte('date', format(weekEnd, 'yyyy-MM-dd'))
        .eq('user_id', session.user.id);

      if (moodError) throw moodError;

      // Calculate average mood
      const moodAverage = moodData?.length 
        ? moodData.reduce((acc, curr) => acc + curr.mood_score, 0) / moodData.length 
        : 0;

      // Get or create journey entry for the week
      const { data: journeyData, error: journeyError } = await supabase
        .from('user_journey')
        .select('*')
        .eq('week_start', format(weekStart, 'yyyy-MM-dd'))
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (journeyError) throw journeyError;

      if (!journeyData) {
        const { data: newJourney, error: createError } = await supabase
          .from('user_journey')
          .insert({
            user_id: session.user.id,
            week_start: format(weekStart, 'yyyy-MM-dd'),
            week_end: format(weekEnd, 'yyyy-MM-dd'),
            mood_average: moodAverage,
            mood_improvement: 0,
            achievements: [],
            reflections: ''
          })
          .select()
          .single();

        if (createError) throw createError;
        setJourneyData(newJourney);
      } else {
        setJourneyData(journeyData);
      }
    } catch (error: any) {
      console.error('Error loading journey data:', error);
      toast({
        title: "Error loading journey data",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadJourneyData();
  }, []);

  return {
    journeyData,
    isLoading,
    loadJourneyData,
  };
};